// @ts-nocheck
// W14a — Shared state between NavegadorShell (persistent map) and sidebar routes.
// Sidebar children publish geoJson + selectedId; shell consumes them to update the persistent SmartMap.
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";

type SmartMapApi = any;

export interface NavegadorMapContextValue {
  /** Set by SmartMap via onApiReady — gives sidebar children access to map helpers (getXYZ, fit, etc). */
  smartMapApiRef: MutableRefObject<SmartMapApi | null>;
  /** GeoJSON overlay drawn on the persistent map. Sidebar routes call setGeoJson to highlight a lote. */
  geoJson: unknown;
  setGeoJson: (g: unknown) => void;
  /** Currently-selected lote/imovel id, kept around for highlight + back-stack continuity. */
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
}

const NavegadorMapContext = createContext<NavegadorMapContextValue | null>(null);

export function NavegadorMapProvider({ children }: { children: ReactNode }) {
  const smartMapApiRef = useRef<SmartMapApi | null>(null);
  const [geoJson, setGeoJson] = useState<unknown>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Memoize so consumer effects depending on the context value don't loop:
  // setGeoJson/setSelectedId from useState are already stable, smartMapApiRef
  // is stable, and value only changes when geoJson or selectedId actually change.
  const value = useMemo(
    () => ({ smartMapApiRef, geoJson, setGeoJson, selectedId, setSelectedId }),
    [geoJson, selectedId],
  );

  return (
    <NavegadorMapContext.Provider value={value}>
      {children}
    </NavegadorMapContext.Provider>
  );
}

export function useNavegadorMap(): NavegadorMapContextValue {
  const ctx = useContext(NavegadorMapContext);
  if (!ctx) throw new Error("useNavegadorMap must be used inside <NavegadorShell>");
  return ctx;
}

/** Optional hook — returns null when not inside the shell, used by shared components
 *  (ImovelList, LoteView, ImovelView) to decide whether to suppress their own map/header. */
export function useOptionalNavegadorMap(): NavegadorMapContextValue | null {
  return useContext(NavegadorMapContext);
}
